import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import Input from './Input';
import Modal from './Modal';
import MultiSelectDropdown from './MultiSelectDropdown';
import { useSnackbar } from '../../contexts/SnackbarContext';

// Icons
const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
);

const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white mix-blend-difference" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
);

interface Subject {
    id: string | number;
    name: string;
    color?: string;
    allowedRooms?: string[];
}

interface Room {
    id: number;
    name?: string;
    room_code?: string;
}

interface SubjectSelectorProps {
    subjects: Subject[];
    rooms: Room[];
    selectedSubjectId?: string | number | null;
    onChange: (subjectId: string | number | null, subjectData?: Subject) => void;
    onAddSubject?: (subjectData: { name: string; color?: string; associatedRooms?: string[] }) => Promise<Subject>;
    placeholder?: string;
    disabled?: boolean;
}

const colorPalette = [
    '#fecaca', '#fca5a5', '#fecdd3', '#f9a8d4',
    '#fed7aa', '#fdba74', '#fef08a', '#fde047',
    '#d9f99d', '#a7f3d0', '#a5f3fc', '#6ee7b7',
    '#bae6fd', '#c7d2fe', '#e9d5ff', '#ddd6fe',
];

const DEFAULT_COLOR = colorPalette[0];

const SubjectSelector: React.FC<SubjectSelectorProps> = ({
    subjects,
    rooms,
    selectedSubjectId,
    onChange,
    onAddSubject,
    placeholder = 'Select Subject',
    disabled = false
}) => {
    const { showSuccess, showError } = useSnackbar();
    const [isOpen, setIsOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState({});
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newSubjectData, setNewSubjectData] = useState({
        name: '',
        color: DEFAULT_COLOR,
        associatedRooms: [] as string[]
    });
    const [isAddingSubject, setIsAddingSubject] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const roomNames = rooms.map(r => r.name || r.room_code || String(r.id)).filter(Boolean);

    const updatePanelPosition = useCallback(() => {
        if (!buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const panelHeightEstimate = 240;
        const margin = 4;

        let top: number | 'auto' = 'auto';
        let bottom: number | 'auto' = 'auto';

        if (spaceBelow >= panelHeightEstimate || spaceBelow >= spaceAbove) {
            top = rect.bottom + window.scrollY + margin;
        } else {
            bottom = window.innerHeight - rect.top - window.scrollY + margin;
        }

        setPanelStyle({
            position: 'absolute',
            top: top === 'auto' ? 'auto' : `${top}px`,
            bottom: bottom === 'auto' ? 'auto' : `${bottom}px`,
            left: `${rect.left + window.scrollX}px`,
            width: `${rect.width}px`,
            zIndex: 1100,
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return;

        updatePanelPosition();

        const handleResize = () => updatePanelPosition();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [isOpen, updatePanelPosition]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const panelElement = document.getElementById('subject-selector-panel');
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                (!panelElement || !panelElement.contains(event.target as Node))
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleSubjectSelect = useCallback((subject: Subject) => {
        onChange(subject.id);
        setIsOpen(false);
    }, [onChange]);

    const handleClearSelection = () => {
        onChange(null);
        setIsOpen(false);
    };

    const handleAddSubjectClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsAddModalOpen(true);
        setIsOpen(false);
    };

    const handleAddSubjectInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewSubjectData(prev => ({ ...prev, [name]: value }));
    };

    const handleColorSelect = (color: string) => {
        setNewSubjectData(prev => ({ ...prev, color }));
    };

    const handleRoomSelectionChange = (selectedRooms: string[]) => {
        setNewSubjectData(prev => ({ ...prev, associatedRooms: selectedRooms }));
    };

    const handleSaveNewSubject = async () => {

        if (!newSubjectData.name.trim()) {
            showError('Subject name is required.');
            return;
        }

        if (!onAddSubject) {
            showError('Add subject functionality is not available.');
            return;
        }

        setIsAddingSubject(true);

        try {
            const dataToSend = {
                name: newSubjectData.name.trim(),
                color: newSubjectData.color,
                associatedRooms: newSubjectData.associatedRooms
            };

            const newSubject = await onAddSubject(dataToSend);

            showSuccess('Subject added successfully!');
            setIsAddModalOpen(false);

            // Select the newly added subject immediately
            if (newSubject && newSubject.id) {
                // Pass the full subject object to ensure it's available
                onChange(newSubject.id, newSubject);
            }
            setNewSubjectData({ name: '', color: DEFAULT_COLOR, associatedRooms: [] });
        } catch (error) {
            console.error('Error adding subject:', error);
            showError(`Failed to add subject: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsAddingSubject(false);
        }
    };

    const getButtonText = () => {
        if (!selectedSubjectId) return placeholder;
        const selectedSubject = subjects.find(s => String(s.id) === String(selectedSubjectId));
        return selectedSubject?.name || placeholder;
    };

    const selectedSubject = subjects.find(s => String(s.id) === String(selectedSubjectId));
    const filteredSubjects = useMemo(() => {
        if (!searchTerm.trim()) {
            return subjects;
        }
        const normalized = searchTerm.trim().toLowerCase();
        return subjects.filter(subject => subject.name?.toLowerCase().includes(normalized));
    }, [subjects, searchTerm]);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    }, []);

    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && filteredSubjects.length > 0) {
            e.preventDefault();
            const firstSubject = filteredSubjects[0];
            if (!firstSubject) return;
            handleSubjectSelect(firstSubject);
        }
    }, [filteredSubjects, handleSubjectSelect]);

    // Panel JSX to be portaled
    const DropdownPanel = isOpen ? (
        <div
            id="subject-selector-panel"
            style={panelStyle}
            className="bg-white shadow-lg rounded-md border border-gray-200 flex flex-col overflow-hidden"
        >
            <div className="p-2 border-b border-gray-200 bg-gray-50">
                <Input
                    id="subject-selector-search"
                    name="subject-selector-search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Filter subjects..."
                    autoFocus
                    required={false}
                    minLength={0}
                    label=""
                />
            </div>
            {/* Scrollable subjects list */}
            <div className="overflow-y-auto max-h-60">
                <ul className="py-1">
                    {filteredSubjects.length > 0 ? (
                        filteredSubjects.map(subject => (
                            <li
                                key={subject.id}
                                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSubjectSelect(subject);
                                }}
                                role="option"
                                aria-selected={String(selectedSubjectId) === String(subject.id)}
                            >
                                <div className="flex items-center w-full">
                                    <div
                                        className="w-3 h-3 rounded border border-gray-300 mr-2 flex-shrink-0"
                                        style={{ backgroundColor: subject.color || DEFAULT_COLOR }}
                                    ></div>
                                    <span className="flex-grow">{subject.name}</span>
                                </div>
                            </li>
                        ))
                    ) : (
                        <li className="px-3 py-2 text-sm text-gray-500 italic">No subjects available</li>
                    )}
                </ul>
            </div>

            {/* Fixed Add Subject Option at the bottom */}
            {onAddSubject && (
                <div className="border-t border-gray-200 flex-shrink-0 bg-white">
                    <div
                        className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer flex items-center"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleAddSubjectClick(e);
                        }}
                    >
                        <PlusIcon />
                        <span className="ml-2 font-medium">Add New Subject</span>
                    </div>
                </div>
            )}
        </div>
    ) : null;

    return (
        <div className="inline-block w-full flex items-center gap-1" ref={dropdownRef}>
            <div className="flex-grow relative" ref={buttonRef}>
                <Button
                    type="button"
                    variant="secondary"
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        if (!disabled) {
                            if (!isOpen) {
                                updatePanelPosition();
                                setIsOpen(true);
                            } else {
                                setIsOpen(false);
                            }
                        }
                    }}
                    disabled={disabled}
                    title="Select subject"
                    aria-label="Select subject"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className="w-full flex items-center text-left px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <div className="flex items-center w-full">
                        {selectedSubject && (
                            <div
                                className="w-3 h-3 rounded border border-gray-300 mr-2 flex-shrink-0"
                                style={{ backgroundColor: selectedSubject.color || DEFAULT_COLOR }}
                            ></div>
                        )}
                        <span className="flex-grow truncate">{getButtonText()}</span>
                        <ChevronDownIcon />
                    </div>
                </Button>
            </div>

            {/* Clear Button */}
            {selectedSubjectId && !disabled && (
                <button
                    type="button"
                    onClick={handleClearSelection}
                    className="p-2 text-gray-500 hover:text-red-600 focus:outline-none focus:ring-1 focus:ring-red-500 rounded-md flex-shrink-0"
                    title="Clear selection"
                    aria-label="Clear selection"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            )}

            {/* Render the panel using a Portal */}
            {DropdownPanel && createPortal(DropdownPanel, document.body)}

            {/* Add Subject Modal */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Subject">
                <div className="space-y-4">
                    <div>
                        <label htmlFor="add-subject-name" className="block text-sm font-medium text-gray-700 mb-1">Subject Name</label>
                        <Input
                            id="add-subject-name"
                            name="name"
                            value={newSubjectData.name}
                            onChange={handleAddSubjectInputChange}
                            placeholder="Enter subject name"
                            required
                            autoFocus
                            disabled={isAddingSubject}
                            label=""
                            minLength={0}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                        <div className="flex flex-wrap gap-1">
                            {colorPalette.map(color => (
                                <button
                                    key={color}
                                    type="button"
                                    className={`w-6 h-6 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500 flex items-center justify-center ${newSubjectData.color === color ? 'ring-2 ring-blue-600 ring-offset-1' : ''}`}
                                    style={{ backgroundColor: color }}
                                    onClick={() => handleColorSelect(color)}
                                    title={color}
                                    disabled={isAddingSubject}
                                >
                                    {newSubjectData.color === color && <CheckIcon />}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Selected: {newSubjectData.color}</p>
                    </div>

                    <div>
                        <label htmlFor="add-subject-rooms" className="block text-sm font-medium text-gray-700 mb-1">
                            Associated Rooms (Optional)
                            <span className="text-xs text-gray-500 ml-1">- Only allow booking in selected rooms</span>
                        </label>
                        <MultiSelectDropdown
                            options={roomNames}
                            selectedOptions={newSubjectData.associatedRooms}
                            onChange={handleRoomSelectionChange}
                            placeholder="Available in all rooms"
                        />
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setIsAddModalOpen(false)}
                            disabled={isAddingSubject}
                            title="Cancel adding subject"
                            aria-label="Cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            disabled={isAddingSubject}
                            onClick={handleSaveNewSubject}
                            title="Add new subject"
                            aria-label="Add New Subject"
                        >
                            {isAddingSubject ? 'Adding...' : 'Add New Subject'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default SubjectSelector;
